import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { ROUTES } from '../../../constants';
import { ColorModeContext } from '../../../index';
import { useTheme } from '@mui/material/styles';
import { brand } from '../../../theme/colors';

// The header is dark in both modes, so it keeps the dark palette's colours.
const iconSx = { color: brand.white, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } };

const Header = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const colorMode = useContext(ColorModeContext);
  const theme = useTheme();

  const [isAuthed, setIsAuthed] = useState<boolean>(!!auth.currentUser);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setIsAuthed(!!user));
    return () => unsub();
  }, [auth]);

  const handleSignOut = () => {
    signOut(auth)
      .then(() => navigate(ROUTES.login))
      .catch(console.error);
  };

  return (
    <AppBar position="sticky" color="default" elevation={1}>
      <Toolbar sx={{ px: 2, minHeight: 56 }}>
        {/* Brand */}
        <Typography
          variant="h6"
          component={Link}
          to={isAuthed ? ROUTES.hub : ROUTES.watchlist}
          sx={{
            flexGrow: 1,
            textDecoration: 'none',
            color: brand.pink,
            fontFamily: 'Indie Flower, cursive',
            fontWeight: 600,
          }}
        >
          Craig&apos;s Hub
        </Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip
            title={`Switch to ${
              theme.palette.mode === 'light' ? 'dark' : 'light'
            } mode`}
          >
            <IconButton
              onClick={colorMode.toggleColorMode}
              sx={{ ...iconSx, color: brand.pink }}
            >
              {theme.palette.mode === 'light' ? (
                <DarkModeIcon />
              ) : (
                <LightModeIcon />
              )}
            </IconButton>
          </Tooltip>

          {isAuthed ? (
            <Tooltip title="Sign Out">
              <IconButton sx={iconSx} onClick={handleSignOut}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Sign In">
              <IconButton
                sx={iconSx}
                onClick={() => navigate(ROUTES.login)}
              >
                <LoginIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;